import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  FormControl,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  IconButton,
  Tooltip,
  InputLabel,
} from "@mui/material";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TextInput from "../../../../../../components/textinput/textinput";
import {
  monthOptions,
  websiteRegex,
  yearOptions,
} from "../../../../../../utils/Constant";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  getOwnershipTypesRequest,
  getMembershipTypesRequest,
  getInspectionFirmDetailsRequest,
} from "../../../../../../redux/inspection-firms/slice";
import Loading from "../../../../../../components/loading/loading";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import { updateInspectionFirmDetailsRequest } from "../../../../../../utils/APICalls";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

interface FormValues {
  displayName?: string;
  aliasName?: string;
  grade?: string;
  website?: string;
  maxCapacity?: string;
  companyId?: string;
  establishedMonth?: string;
  establishedYear?: string;
  ownedBy?: string[];
  memberships?: string[];
  otherMemberships?: string;
}

export default function EditFirmDetails({
  showDialog,
  closeDialog,
  data,
}: props) {
  const dispatch = useDispatch();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { ownershipTypes, membershipTypes, loading, error } = useSelector(
    (state: any) => state.inspectionFirms
  );
  const [otherMembershipsChecked, setOtherMembershipsChecked] = useState(false);

  useEffect(() => {
    dispatch(getOwnershipTypesRequest());
    dispatch(getMembershipTypesRequest());
  }, [dispatch]);

  useEffect(() => {
    setOtherMembershipsChecked(
      data?.otherMemberships !== null &&
      data?.otherMemberships !== undefined &&
      data?.otherMemberships !== ""
    );
  }, [data]);

  const ValidationSchema = yup.object({
    displayName: yup.string().required("This field can't be empty"),
    website: yup.string().matches(websiteRegex, "Invalid Website"),
    maxCapacity: yup
      .number()
      .typeError("This field can't be empty")
      .required("This field can't be empty")
      .min(1, "This field requires a value above 0"),
    grade: yup
      .string()
      .test("is-number", "Grade must be a valid number", (value) => {
        if (!value || value === "") return true;
        return !isNaN(Number(value));
      })
      .test("is-valid-range", "Grade must be between 0 and 4", (value) => {
        if (!value || value === "") return true;
        const numValue = Number(value);
        return numValue >= 0 && numValue <= 4;
      })
      .notRequired(),
    establishedMonth: yup.string().notRequired(),
    establishedYear: yup.string().notRequired(),
    otherMemberships: otherMembershipsChecked
      ? yup.string().required("This field can't be empty")
      : yup.string().notRequired(),
  }).test(
    "month-year-pair",
    "Both established month and year are required",
    function (values) {
      const month = values?.establishedMonth;
      const year = values?.establishedYear;

      if (!month && !year) {
        return true;
      }

      if (!month || !year) {
        return this.createError({
          path: !month ? "establishedMonth" : "establishedYear",
          message: !month
            ? "Established month is required"
            : "Established year is required",
        });
      }

      return true;
    }
  );;

  const formik = useFormik<FormValues>({
    initialValues: {
      displayName: data?.displayName || "",
      aliasName: data?.aliasName || "",
      grade: data?.grade || "",
      website: data?.website || "",
      maxCapacity: data?.maxCapacity || "",
      companyId: data?.companyId || "",
      establishedMonth: data?.establishedOn?.month || null,
      establishedYear: data?.establishedOn?.year || null,
      ownedBy: data?.ownerships?.map((o: any) => o.ownershipType) || [],
      memberships: data?.memberships?.map((m: any) => m.membershipType) || [],
      otherMemberships: data?.otherMemberships || "",
    },
    validationSchema: ValidationSchema,
    onSubmit: async (values) => {
      const isValid = await formik.validateForm();
      if (!isValid) {
        formik.setTouched({
          displayName: true,
          grade: true,
          website: true,
          maxCapacity: true,
          establishedMonth: true,
          establishedYear: true,
          ownedBy: true,
          memberships: true,
          otherMemberships: formik.values.otherMemberships !== undefined,
        });
        return;
      }
      updateInspectionFirmDetails(values);
    },
    enableReinitialize: true,
  });

  const updateInspectionFirmDetails = async (values: any) => {
    let finalWebsite = null;
    if (values.website.length > 0) {
      finalWebsite = values.website.includes("http")
        ? values.website
        : `https://${values.website}`;
    }
    const payload = {
      id: data?.id,
      displayName: values.displayName,
      aliasName: values.aliasName || "",
      grade: values.grade !== "" && values.grade != null ? values.grade : null,
      maxCapacity: values.maxCapacity,
      establishedOn: {
        month: values.establishedMonth,
        year: values.establishedYear,
      },
      ownedBy: values.ownedBy,
      memberships: values.memberships,
      otherMemberships: otherMembershipsChecked ? values.otherMemberships : "",
      website: finalWebsite,
    };
    setShowLoading(true);
    const response = await updateInspectionFirmDetailsRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getInspectionFirmDetailsRequest({ id: data?.id }));
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  return (
    <DialogContainer
      title="Inspection Firm - Firm Details"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Box
            sx={{
              width: 192,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
              Max Capacity
            </Typography>
            <Tooltip
              title="This setting is internal only. Sets the maximum number of jobs assigned to this inspection firm at a time. Jobs can still be manually assigned above this maximum, but jobs will not be auto-assigned once this number has been reached."
              arrow
              placement="top"
            >
              <IconButton sx={{ p: 0, mr: 2 }}>
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextInput
            className="field-input"
            id="maxCapacity"
            name="maxCapacity"
            label="Max Capacity"
            variant="outlined"
            placeholder="Max Capacity"
            type="number"
            slotProps={{
              htmlInput: {
                min: 1,
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "-" || e.key === "e") {
                    e.preventDefault();
                  }
                },
                onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                  const paste = e.clipboardData.getData("text");
                  if (paste.includes("-") || paste.includes("e")) {
                    e.preventDefault();
                  }
                },
              },
            }}
            value={formik.values.maxCapacity}
            onChange={formik.handleChange}
            error={
              formik.touched.maxCapacity && Boolean(formik.errors.maxCapacity)
            }
            helperText={
              formik.touched.maxCapacity && formik.errors.maxCapacity
            }
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Inspection Firm Name</Typography>
          <TextInput
            className="field-input"
            id="displayName"
            name="displayName"
            variant="outlined"
            placeholder="Inspection Firm Name"
            value={formik.values.displayName}
            onChange={formik.handleChange}
            error={
              formik.touched.displayName && Boolean(formik.errors.displayName)
            }
            helperText={formik.touched.displayName && formik.errors.displayName}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">
            Inspection Firm Alias <br />
            (Optional)
          </Typography>
          <TextInput
            className="field-input"
            id="aliasName"
            name="aliasName"
            variant="outlined"
            placeholder="Inspection Firm Alias"
            value={formik.values.aliasName}
            onChange={formik.handleChange}
            error={formik.touched.aliasName && Boolean(formik.errors.aliasName)}
            helperText={formik.touched.aliasName && formik.errors.aliasName}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Grade (GPA)</Typography>
          <TextInput
            className="field-input"
            id="grade"
            name="grade"
            variant="outlined"
            placeholder="Grade"
            type="number"
            slotProps={{
              htmlInput: {
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (
                    e.key === "-" ||
                    e.key === "e" ||
                    e.key === "E" ||
                    e.key === "+"
                  ) {
                    e.preventDefault();
                  }
                },
                onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                  const paste = e.clipboardData.getData("text");
                  if (
                    paste.includes("-") ||
                    paste.includes("e") ||
                    paste.includes("E") ||
                    paste.includes("+")
                  ) {
                    e.preventDefault();
                  }
                },
              },
            }}
            value={formik.values.grade}
            onChange={formik.handleChange}
            error={formik.touched.grade && Boolean(formik.errors.grade)}
            helperText={formik.touched.grade && formik.errors.grade}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Company ID</Typography>
          <Typography>{formik.values.companyId}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Website</Typography>
          <TextInput
            className="field-input"
            id="website"
            name="website"
            label="Website"
            variant="outlined"
            placeholder="Website"
            value={formik.values.website}
            onChange={formik.handleChange}
            error={formik.touched.website && Boolean(formik.errors.website)}
            helperText={formik.touched.website && formik.errors.website}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">
            When Was the Business Established?
          </Typography>
          <FormControl sx={{ width: "200px", marginRight: "20px" }}>
            <InputLabel id="established-month-label">Month</InputLabel>
            <Select
              labelId="established-month-label"
              id="establishedMonth"
              name="establishedMonth"
              value={formik.values.establishedMonth}
              onChange={formik.handleChange}
              error={
                formik.touched.establishedMonth &&
                Boolean(formik.errors.establishedMonth)
              }
              label="Month"
            >
              {monthOptions.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
            {Boolean(formik.errors.establishedMonth) &&
              formik.touched.establishedMonth && (
                <Typography
                  sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                  color="error"
                >
                  This field can't be empty
                </Typography>
              )}
          </FormControl>
          <FormControl sx={{ width: "200px" }}>
            <InputLabel id="established-year-label">Year</InputLabel>
            <Select
              labelId="established-year-label"
              id="establishedYear"
              name="establishedYear"
              value={formik.values.establishedYear}
              onChange={formik.handleChange}
              error={
                formik.touched.establishedYear &&
                Boolean(formik.errors.establishedYear)
              }
              label="Year"
            >
              {yearOptions.map((year) => (
                <MenuItem key={year.value} value={year.value}>
                  {year.label}
                </MenuItem>
              ))}
            </Select>
            {Boolean(formik.errors.establishedYear) &&
              formik.touched.establishedYear && (
                <Typography
                  sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                  color="error"
                >
                  This field can't be empty
                </Typography>
              )}
          </FormControl>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Company Owned By</Typography>
          <FormGroup>
            {ownershipTypes.map((type: any) => (
              <FormControlLabel
                key={type.ownershipType}
                control={
                  <Checkbox
                    checked={formik.values.ownedBy?.includes(
                      type.ownershipType
                    )}
                    onBlur={() => formik.setFieldTouched("ownedBy", true)}
                    onChange={() => {
                      const set = new Set(formik.values.ownedBy);
                      if (set.has(type.ownershipType)) {
                        set.delete(type.ownershipType);
                      } else {
                        set.add(type.ownershipType);
                      }
                      formik.setFieldValue("ownedBy", Array.from(set));
                    }}
                    name={type.ownershipType}
                  />
                }
                label={type.displayName}
              />
            ))}
          </FormGroup>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Memberships</Typography>
          <FormGroup sx={{ width: "calc(100% - 180px)" }}>
            {membershipTypes.map((type: any) => (
              <FormControlLabel
                key={type.membershipType}
                control={
                  <Checkbox
                    checked={formik.values.memberships?.includes(
                      type.membershipType
                    )}
                    onBlur={() => formik.setFieldTouched("memberships", true)}
                    onChange={() => {
                      const set = new Set(formik.values.memberships);
                      if (set.has(type.membershipType)) {
                        set.delete(type.membershipType);
                      } else {
                        set.add(type.membershipType);
                      }
                      formik.setFieldValue("memberships", Array.from(set));
                    }}
                    name={type.displayName}
                  />
                }
                label={type.displayName}
              />
            ))}
            <FormControlLabel
              control={
                <Checkbox
                  checked={otherMembershipsChecked}
                  onChange={(e) => {
                    setOtherMembershipsChecked(e.target.checked);
                  }}
                  name="otherMemberships"
                />
              }
              label="Other"
            />
            {otherMembershipsChecked && (
              <TextField
                sx={{ ml: 4, width: "calc(100% - 32px)" }}
                id="membership-other"
                name="otherMemberships"
                variant="outlined"
                multiline
                value={formik.values.otherMemberships}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.otherMemberships &&
                  Boolean(formik.errors.otherMemberships)
                }
                helperText={
                  formik.touched.otherMemberships &&
                  formik.errors.otherMemberships
                }
              />
            )}
          </FormGroup>
        </Box>
      </form>
      {(loading || showLoading) && <Loading />}
      {(error || showErrorNotification) && (
        <ErrorNotification
          show={showErrorNotification}
          message={error || errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
